import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatTitle } from '../../utils/formatTitle'
import './IncyiaGantt.css'

export type IncyiaGanttRow = {
  id: string
  name: string
  from: Date
  to: Date
}

type ViewMode = 'days' | 'weeks'
type DragKind = 'move' | 'resize-start' | 'resize-end'

type Props = {
  rows: IncyiaGanttRow[]
  rangeStart?: Date
  rangeEnd?: Date
  /** Persist date changes (drag / resize). Enables editing when set. */
  onChange?: (id: string, from: Date, to: Date) => void | Promise<void>
  editable?: boolean
}

const MS_DAY = 86400000
const MIN_DAY_W = 40
const MIN_WEEK_W = 64
/** Prevent DOM blow-ups if a bar is dragged far outside the plan. */
const MAX_DAYS = 420

function startOfDay(d: Date) {
  const x = new Date(d)
  if (Number.isNaN(x.getTime())) return new Date(NaN)
  return new Date(x.getFullYear(), x.getMonth(), x.getDate())
}

function addDays(d: Date, n: number) {
  return new Date(d.getTime() + n * MS_DAY)
}

function daysBetween(a: Date, b: Date) {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / MS_DAY)
}

function isValidDate(d: Date) {
  return d instanceof Date && !Number.isNaN(d.getTime())
}

function parseDate(value: Date | string | number) {
  const d = value instanceof Date ? value : new Date(value)
  return isValidDate(d) ? d : null
}

function formatRangeLabel(from: Date, to: Date, locale: string) {
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }
  return `${from.toLocaleDateString(locale, opts)} → ${to.toLocaleDateString(locale, opts)}`
}

function monthName(d: Date, locale: string) {
  return d.toLocaleDateString(locale, { month: 'long' }).toUpperCase()
}

function weekdayShort(d: Date, locale: string) {
  return d.toLocaleDateString(locale, { weekday: 'short' })
}

function localDateKey(d: Date) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function normalizeRows(rows: IncyiaGanttRow[], locale: string) {
  return rows
    .map((r) => {
      const fromRaw = parseDate(r.from)
      const toRaw = parseDate(r.to)
      if (!fromRaw || !toRaw) return null
      const from = startOfDay(fromRaw)
      let to = startOfDay(toRaw)
      if (to.getTime() < from.getTime()) to = addDays(from, 1)
      return {
        id: r.id,
        name: r.name || '—',
        from,
        to,
        label: formatRangeLabel(from, to, locale),
      }
    })
    .filter((r): r is LocalRow => !!r)
}

type LocalRow = IncyiaGanttRow & { label: string }

type DragState = {
  id: string
  kind: DragKind
  startX: number
  originFrom: Date
  originTo: Date
  currentFrom: Date
  currentTo: Date
  moved: boolean
}

function applyDelta(
  kind: DragKind,
  from: Date,
  to: Date,
  deltaDays: number
): { from: Date; to: Date } {
  if (deltaDays === 0) return { from, to }
  if (kind === 'move') {
    return { from: addDays(from, deltaDays), to: addDays(to, deltaDays) }
  }
  if (kind === 'resize-start') {
    let nextFrom = addDays(from, deltaDays)
    if (nextFrom.getTime() > to.getTime()) nextFrom = to
    return { from: nextFrom, to }
  }
  let nextTo = addDays(to, deltaDays)
  if (nextTo.getTime() < from.getTime()) nextTo = from
  return { from, to: nextTo }
}

function computeRange(
  source: { from: Date; to: Date }[],
  rangeStart?: Date,
  rangeEnd?: Date
) {
  if (!source.length) {
    const today = startOfDay(new Date())
    return { d0: today, dayCount: 14 }
  }
  const min = rangeStart
    ? startOfDay(rangeStart)
    : startOfDay(new Date(Math.min(...source.map((r) => r.from.getTime()))))
  const max = rangeEnd
    ? startOfDay(rangeEnd)
    : startOfDay(new Date(Math.max(...source.map((r) => r.to.getTime()))))
  const d0 = addDays(min, -1)
  const d1 = addDays(max, 3)
  const dayCount = Math.min(
    MAX_DAYS,
    Math.max(1, daysBetween(d0, d1) + 1)
  )
  return { d0, dayCount }
}

/** Shared INCYIA Gantt — visual match to redesign HTML, with optional drag/resize. */
export default function IncyiaGantt({
  rows,
  rangeStart,
  rangeEnd,
  onChange,
  editable = !!onChange,
}: Props) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'en'
  const [mode, setMode] = useState<ViewMode>('days')
  const [localRows, setLocalRows] = useState<LocalRow[]>(() =>
    normalizeRows(rows, locale)
  )
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const dragRef = useRef<DragState | null>(null)
  const onChangeRef = useRef(onChange)
  const modeRef = useRef(mode)
  const savingRef = useRef(false)
  onChangeRef.current = onChange
  modeRef.current = mode

  // Stable timeline from props (not live drag positions)
  const baseRange = useMemo(
    () => computeRange(normalizeRows(rows, locale), rangeStart, rangeEnd),
    [rows, rangeStart, rangeEnd, locale]
  )

  // Timeline: freeze while dragging so the grid does not rebuild under the pointer
  const [frozenRange, setFrozenRange] = useState<{
    d0: Date
    dayCount: number
  } | null>(null)
  const { d0, dayCount } = frozenRange ?? baseRange
  const d0Ref = useRef(d0)
  const dayCountRef = useRef(dayCount)
  d0Ref.current = d0
  dayCountRef.current = dayCount

  useEffect(() => {
    if (dragRef.current || savingRef.current) return
    const next = normalizeRows(rows, locale)
    setLocalRows((prev) => {
      if (!next.length && prev.length) return prev
      return next
    })
    setFrozenRange(null)
  }, [rows, locale])

  const days = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => addDays(d0, i)),
    [d0, dayCount]
  )

  const months = useMemo(() => {
    const out: { label: string; count: number }[] = []
    let cur: { label: string; count: number } | null = null
    days.forEach((d) => {
      const label = monthName(d, locale)
      if (!cur || cur.label !== label) {
        cur = { label, count: 0 }
        out.push(cur)
      }
      cur.count++
    })
    return out
  }, [days, locale])

  const weeks = useMemo(() => {
    const out: { label: string; startIdx: number; count: number }[] = []
    let cur: { label: string; startIdx: number; count: number } | null = null
    days.forEach((d, i) => {
      const day = d.getDay()
      const mondayOffset = day === 0 ? -6 : 1 - day
      const key = localDateKey(addDays(d, mondayOffset))
      if (!cur || cur.label !== key) {
        cur = { label: key, startIdx: i, count: 0 }
        out.push(cur)
      }
      cur.count++
    })
    return out
  }, [days])

  const trackCols = mode === 'days' ? dayCount : weeks.length
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [colWidth, setColWidth] = useState(
    mode === 'days' ? MIN_DAY_W : MIN_WEEK_W
  )
  const colWidthRef = useRef(colWidth)
  colWidthRef.current = colWidth

  // Stretch columns to fill the available track width (full page)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const measure = () => {
      const leftCol = el.querySelector('.incyia-gantt-left') as HTMLElement | null
      const leftW = leftCol?.offsetWidth ?? 280
      const avail = Math.max(0, el.clientWidth - leftW)
      const minW = mode === 'days' ? MIN_DAY_W : MIN_WEEK_W
      const next = Math.max(minW, avail / Math.max(1, trackCols))
      setColWidth(next)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [mode, trackCols, localRows.length])

  const trackWidth = trackCols * colWidth

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current
      if (!drag) return
      const col = colWidthRef.current || MIN_DAY_W
      const stepDays = modeRef.current === 'days' ? 1 : 7
      const deltaCols = Math.round((e.clientX - drag.startX) / col)
      const deltaDays = deltaCols * stepDays
      if (deltaDays !== 0) drag.moved = true
      const next = applyDelta(
        drag.kind,
        drag.originFrom,
        drag.originTo,
        deltaDays
      )
      drag.currentFrom = next.from
      drag.currentTo = next.to
      setLocalRows((prev) =>
        prev.map((r) =>
          r.id === drag.id
            ? {
                ...r,
                from: next.from,
                to: next.to,
                label: formatRangeLabel(next.from, next.to, locale),
              }
            : r
        )
      )
    }

    const onUp = () => {
      const drag = dragRef.current
      if (!drag) return
      dragRef.current = null
      setDraggingId(null)
      if (!drag.moved) {
        setFrozenRange(null)
        return
      }
      const cb = onChangeRef.current
      if (!cb) {
        setFrozenRange(null)
        return
      }
      savingRef.current = true
      void Promise.resolve(cb(drag.id, drag.currentFrom, drag.currentTo))
        .catch(() => {
          // Keep optimistic dates; parent may refresh
        })
        .finally(() => {
          savingRef.current = false
          setFrozenRange(null)
        })
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [locale])

  const beginDrag = useCallback(
    (e: React.PointerEvent, row: LocalRow, kind: DragKind) => {
      if (!editable || !onChangeRef.current) return
      e.preventDefault()
      e.stopPropagation()
      setFrozenRange({ d0: d0Ref.current, dayCount: dayCountRef.current })
      dragRef.current = {
        id: row.id,
        kind,
        startX: e.clientX,
        originFrom: row.from,
        originTo: row.to,
        currentFrom: row.from,
        currentTo: row.to,
        moved: false,
      }
      setDraggingId(row.id)
    },
    [editable]
  )

  if (!localRows.length) {
    return (
      <p className="detail-empty-msg">
        {t('pipelineDetailPage.empty.gantt', {
          defaultValue:
            'Attach projects with start and end dates to see the Gantt chart.',
        })}
      </p>
    )
  }

  const dayStyle = { flex: `1 0 ${colWidth}px`, minWidth: colWidth, width: colWidth }

  return (
    <div className="incyia-gantt-wrap">
      <div className="incyia-gantt-toolbar">
        <div>
          <h2 className="incyia-gantt-title">
            {t('pipelineDetailPage.gantt', { defaultValue: 'Gantt chart' })}
          </h2>
          <p className="incyia-gantt-hint">
            {editable
              ? t('pipelineDetailPage.ganttHintEdit', {
                  defaultValue:
                    'Drag a bar to move it, or pull the edges to change dates. Weekends shaded.',
                })
              : t('pipelineDetailPage.ganttHint', {
                  defaultValue:
                    'Weekends shaded. Scroll sideways to follow the plan.',
                })}
          </p>
        </div>
        <div className="incyia-gantt-chips" role="group" aria-label="Scale">
          <button
            type="button"
            className={`incyia-gantt-chip${mode === 'days' ? ' is-on' : ''}`}
            onClick={() => setMode('days')}
          >
            {t('pipelineDetailPage.ganttDays', { defaultValue: 'Days' })}
          </button>
          <button
            type="button"
            className={`incyia-gantt-chip${mode === 'weeks' ? ' is-on' : ''}`}
            onClick={() => setMode('weeks')}
          >
            {t('pipelineDetailPage.ganttWeeks', { defaultValue: 'Weeks' })}
          </button>
        </div>
      </div>

      <div className="incyia-gantt">
        <div className="incyia-gantt-scroll" ref={scrollRef}>
          <div
            className="incyia-gantt-inner"
            style={{ minWidth: `max(100%, ${280 + trackWidth}px)` }}
          >
            <div className="incyia-gantt-row is-head">
              <div className="incyia-gantt-left">
                <b>{t('Name', { defaultValue: 'Name' })}</b>
                <span>
                  {t('pipelineDetailPage.ganttFromTo', {
                    defaultValue: 'From → To',
                  })}
                </span>
              </div>
              <div
                className="incyia-gantt-track is-header"
                style={{ width: trackWidth, flex: `0 0 ${trackWidth}px` }}
              >
                {mode === 'days' ? (
                  <>
                    <div className="incyia-gantt-month">
                      {months.map((m) => (
                        <span
                          key={m.label + m.count}
                          className="incyia-gantt-m"
                          style={{ width: m.count * colWidth }}
                        >
                          {m.label}
                        </span>
                      ))}
                    </div>
                    <div className="incyia-gantt-days">
                      {days.map((d) => {
                        const we = d.getDay() === 0 || d.getDay() === 6
                        return (
                          <div
                            key={localDateKey(d)}
                            className={`incyia-gantt-day${we ? ' is-we' : ''}`}
                            style={dayStyle}
                          >
                            {weekdayShort(d, locale)}
                            <b>{d.getDate()}</b>
                          </div>
                        )
                      })}
                    </div>
                  </>
                ) : (
                  <div className="incyia-gantt-days">
                    {weeks.map((w) => {
                      const start = days[w.startIdx]
                      const label = start.toLocaleDateString(locale, {
                        day: 'numeric',
                        month: 'short',
                      })
                      return (
                        <div
                          key={w.label}
                          className="incyia-gantt-day is-week"
                          style={dayStyle}
                        >
                          {t('pipelineDetailPage.ganttWeekOf', {
                            defaultValue: 'W/o',
                          })}
                          <b>{label}</b>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {localRows.map((r) => {
              const grid =
                mode === 'days'
                  ? days.map((d) => {
                      const we = d.getDay() === 0 || d.getDay() === 6
                      return (
                        <div
                          key={localDateKey(d)}
                          className={`incyia-gantt-day${we ? ' is-we' : ''}`}
                          style={dayStyle}
                        >
                          &nbsp;
                        </div>
                      )
                    })
                  : weeks.map((w) => (
                      <div
                        key={w.label}
                        className="incyia-gantt-day is-week"
                        style={dayStyle}
                      >
                        &nbsp;
                      </div>
                    ))

              let leftPct = 0
              let widthPct = 100 / trackCols
              if (mode === 'days') {
                const s = Math.max(0, daysBetween(d0, r.from))
                const e = Math.max(s, daysBetween(d0, r.to))
                const span = Math.max(1, e - s + 1)
                leftPct = (s / trackCols) * 100
                widthPct = (span / trackCols) * 100
              } else {
                const sDay = daysBetween(d0, r.from)
                const eDay = daysBetween(d0, r.to)
                let startW = 0
                let endW = weeks.length - 1
                weeks.forEach((w, i) => {
                  const wEnd = w.startIdx + w.count - 1
                  if (sDay >= w.startIdx && sDay <= wEnd) startW = i
                  if (eDay >= w.startIdx && eDay <= wEnd) endW = i
                })
                const span = Math.max(1, endW - startW + 1)
                leftPct = (startW / trackCols) * 100
                widthPct = (span / trackCols) * 100
              }

              const displayName = formatTitle(r.name)

              return (
                <div className="incyia-gantt-row" key={r.id}>
                  <div className="incyia-gantt-left">
                    <b>{displayName}</b>
                    <span>{r.label}</span>
                  </div>
                  <div
                    className="incyia-gantt-track"
                    style={{ width: trackWidth, flex: `0 0 ${trackWidth}px` }}
                  >
                    {grid}
                    <div
                      className={`incyia-gantt-bar${editable ? ' is-editable' : ''}${draggingId === r.id ? ' is-dragging' : ''}`}
                      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                      title={`${displayName} · ${r.label}`}
                      onPointerDown={(e) => beginDrag(e, r, 'move')}
                    >
                      {editable ? (
                        <span
                          className="incyia-gantt-handle is-start"
                          onPointerDown={(e) =>
                            beginDrag(e, r, 'resize-start')
                          }
                        />
                      ) : null}
                      <span className="incyia-gantt-bar-label">
                        {displayName}
                      </span>
                      {editable ? (
                        <span
                          className="incyia-gantt-handle is-end"
                          onPointerDown={(e) => beginDrag(e, r, 'resize-end')}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
